import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_82: DocMigration = {
  maxSchemaVersion: "0.0.82",
  description: "split foundation settings",
  apply(json: any) {
    migrateDoc0083RenameDrivewaySettings(json);
    migrateDoc0083SplitFoundationSettings(json);
    return json;
  },
};

function migrateDoc0083RenameDrivewaySettings(json: any) {
  if (json.settings) {
    for (const settingId in json.settings) {
      if (json.settings[settingId].type === "DRIVEWAY_SETTINGS") {
        json.settings[settingId].type = "GARAGE_SETTINGS";
      }
    }
  }
  if (json.elevations) {
    for (const elevation in json.elevations) {
      if (!json.elevations[elevation].settingsOverride?.driveway) {
        continue;
      }
      const settingsId = json.elevations[elevation].settingsOverride["driveway"];
      json.elevations[elevation].settingsOverride["garage"] = settingsId;
      delete json.elevations[elevation].settingsOverride["driveway"];
    }
  }
  if (json.settingsOverride?.driveway) {
    const settingsId = json.settingsOverride.driveway;
    json.settingsOverride["garage"] = settingsId;
    delete json.settingsOverride["driveway"];
  }
  return json;
}

function migrateDoc0083SplitFoundationSettings(json: any) {
  function migrateSetting(
    sourceSettings: any,
    targetSettings: any,
    propertyName: string,
    newPropertyName = propertyName,
  ) {
    if (sourceSettings[propertyName] !== undefined) {
      targetSettings[newPropertyName] = sourceSettings[propertyName];
      delete sourceSettings[propertyName];
    }
  }

  function splitFoundationSettings(foundationSettings: any) {
    const garageSettings = {};
    const porchSettings = {};
    const foundationSlabSettings = {};
    const foundationSlabMonoSettings = {};
    const foundationSlabStemSettings = {};

    migrateSetting(foundationSettings, garageSettings, "garageStepDistance", "garageDatumOffset");
    migrateSetting(foundationSettings, garageSettings, "garageCurbWidth");
    migrateSetting(foundationSettings, porchSettings, "porchStepDistance", "porchDatumOffset");

    // slab
    if (foundationSettings["slabDepth"] !== undefined) {
      foundationSlabSettings["garageSlabDepth"] = foundationSettings["slabDepth"];
      foundationSlabSettings["porchSlabDepth"] = foundationSettings["slabDepth"];
      migrateSetting(foundationSettings, foundationSlabSettings, "slabDepth", "interiorSlabDepth");
    }

    // brickLedge
    if (foundationSettings["brickLedgeType"] !== undefined) {
      const targetSubType = foundationSettings["foundationSubSystemType"] ?? "MONOLITHIC";
      const targetSettings = targetSubType === "MONOLITHIC" ? foundationSlabMonoSettings : foundationSlabStemSettings;
      migrateSetting(foundationSettings, targetSettings, "brickLedgeType");
    }

    // stem wall
    migrateSetting(foundationSettings, foundationSlabStemSettings, "footingDepth");
    migrateSetting(foundationSettings, foundationSlabStemSettings, "stemWallWidth");
    migrateSetting(foundationSettings, foundationSlabStemSettings, "stemWallInsetType");
    migrateSetting(foundationSettings, foundationSlabStemSettings, "stemWallInset");

    return {
      garageSettings,
      porchSettings,
      foundationSlabSettings,
      foundationSlabMonoSettings,
      foundationSlabStemSettings,
    };
  }

  if (json.settings) {
    function createOrUpdateSettings(settings: any, settingsId = uid(), type?: string) {
      const oldSettings = json.settings[settingsId] || { type };
      const newSettings = {
        ...oldSettings,
        ...settings,
      };
      json.settings[settingsId] = newSettings;
      return settingsId;
    }

    function migrateSettingsOverride(settingsOverride: any) {
      if (!settingsOverride || !settingsOverride.foundation) {
        return;
      }

      const foundationSettingsId = settingsOverride.foundation;
      const foundationSettings = json.settings[foundationSettingsId];
      const {
        garageSettings,
        porchSettings,
        foundationSlabSettings,
        foundationSlabMonoSettings,
        foundationSlabStemSettings,
      } = splitFoundationSettings(foundationSettings);

      if (Object.keys(foundationSettings).length === 1) {
        delete settingsOverride["foundation"];
        delete json.settings[foundationSettingsId];
      }

      if (Object.keys(garageSettings).length) {
        const id = createOrUpdateSettings(garageSettings, settingsOverride.garage, "GARAGE_SETTINGS");
        settingsOverride.garage = id;
      }

      if (Object.keys(porchSettings).length) {
        const id = createOrUpdateSettings(porchSettings, settingsOverride.porch, "PORCH_SETTINGS");
        settingsOverride.porch = id;
      }

      if (Object.keys(foundationSlabSettings).length) {
        const id = createOrUpdateSettings(foundationSlabSettings, uid(), "FOUNDATION_SLAB_SETTINGS");
        settingsOverride.foundationSlab = id;
      }

      if (Object.keys(foundationSlabMonoSettings).length) {
        const id = createOrUpdateSettings(foundationSlabMonoSettings, uid(), "FOUNDATION_SLAB_SETTINGS_MONO");
        settingsOverride.foundationSlabMonolithic = id;
      }

      if (Object.keys(foundationSlabStemSettings).length) {
        const id = createOrUpdateSettings(foundationSlabStemSettings, uid(), "FOUNDATION_SLAB_SETTINGS_STEM");
        settingsOverride.foundationSlabStemWall = id;
      }
    }

    // migrate elevation settings
    if (json.elevations) {
      for (const elevation in json.elevations) {
        const settingsOverride = json.elevations[elevation].settingsOverride;
        migrateSettingsOverride(settingsOverride);
      }
    }

    // migrate default settings
    migrateSettingsOverride(json.settingsOverride);
  }

  return json;
}

export default migration_0_0_82;
